import { JobDetailsNewCollection } from './../dataTypes/JobDetailsNewCollection';
import {
  useQuery,
  hashQueryKey,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
} from "react-query";
import {
  runTransaction,
  onSnapshot,
  doc,
  collection,
  collectionGroup,
  getCountFromServer,
  query,
  where,
  orderBy,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  limit,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db, client, createQuery, format } from "../providers/database";
import { JobDescription } from "../dataTypes/JobDescription";
import { File } from "../dataTypes/Utilities";
import {
  collectionNames,
  subCollectionNames,
} from "./dictionary/collectionNames";
import { list } from "firebase/storage";

export function useAllListings(trigger: any) {
  return useQuery(
    ["Listings"],
    createQuery(() =>
      query(collection(db, collectionNames.listings), orderBy("title", "asc")),
    ),
    { enabled: !!trigger },
  );
}

export function useListing(id: string) {
  return useQuery(
    ["employer", { id }],
    createQuery(() => doc(db, collectionNames.listings, id)),
    { enabled: !!id },
  );
}

export function useAllListingsToInviteJob() {
  return useQuery(
    ["Listings"],
    async () => {
      const q = query(
        collection(db, collectionNames.listings),
        orderBy("title", "asc"),
      );
      const querySnapshot = await getDocs(q);
      const listings = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return listings;
    },
    {
      staleTime: 60000, // Example: Cache for 1 minute
    },
  );
}

export function useAllListingsJobForNewEmp(companyId:string) {
  return useQuery(
    ["JobDetailsNewCollection", companyId],
    async () => {
      // Create the base query
      let q = query(collection(db, "JobDetailsNewCollection"));
      if (companyId) {
        q = query(
          collection(db, "JobDetailsNewCollection"),
          where("companyId", "==", companyId),
         
        );
      }

      const querySnapshot = await getDocs(q);
      const listings = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return listings;
    },
    {
      enabled: !!companyId, // Ensures the query runs only if companyId is provided
    }
  );
}
export async function getAllArchivedJobsForCompany(companyId:string) {
  try {
    if (!companyId) {
      throw new Error("companyId is required");
    }

    const q = query(
      collection(db, collectionNames.ArchivedJobs),
      where("companyId", "==", companyId)
    );

    const querySnapshot = await getDocs(q);

    const listings = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return listings;
  } catch (error) {
    console.error("Error fetching archived jobs:", error);
    throw error;
  }
}


export function useAllAppliedJobsByUID(uid:string) {
  return useQuery(
    [collectionNames.applicationTracker, uid],
    async () => {
      // Create the base query
      let q = query(collection(db, collectionNames.applicationTracker));
      if (uid) {
        q = query(
          collection(db, collectionNames.applicationTracker),
          where("userId", "==", uid),
          where("appliedConfirmation", "==", "yes")
         
        );
      }

      const querySnapshot = await getDocs(q);
      const listings = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return listings;
    },
    {
      enabled: !!uid, // Ensures the query runs only if uid is provided
    }
  );
}

export async function getAllAppliedJobsByUID(uid:string) {
  try {
    if (!uid) {
      return [];
    }

    // Create query
    const q = query(
      collection(db, collectionNames.applicationTracker),
      where("userId", "==", uid)
    );

    const querySnapshot = await getDocs(q);

    const listings = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return listings;
  } catch (error) {
    console.error("Error fetching applied jobs:", error);
    throw error;
  }
}

type ApplicationTrackerEntry = {
  id: string;
  userId?: string;
  jobId?: string;
  appliedConfirmation?: string | null;
  [key: string]: unknown;
};

/** Confirmation for a specific job only (avoids leaking Yes from other jobs). */
export async function getAppliedConfirmationForJob(
  uid: string,
  jobId: string,
): Promise<ApplicationTrackerEntry | null> {
  if (!uid || !jobId) {
    return null;
  }

  const appliedJobs =
    (await getAllAppliedJobsByUID(uid)) as ApplicationTrackerEntry[];
  return appliedJobs.find((job) => job.jobId === jobId) ?? null;
}



export function useJobByIdForNewEmp(jobId: string | undefined) {
  return useQuery(
    ["JobDetailsNewCollection", jobId],
    async () => {
      if (!jobId) {
        throw new Error("Job ID is required to fetch the job details.");
      }

      // Try to fetch from active jobs
      const jobDocRef = doc(db, "JobDetailsNewCollection", jobId);
      let docSnapshot = await getDoc(jobDocRef);

      if (docSnapshot.exists()) {
        return {
          id: docSnapshot.id,
          isArchived: false,
          ...docSnapshot.data(),
        };
      }

      // If not found, try fetching from archived jobs
      const archivedDocRef = doc(db, "ArchivedJobs", jobId);
      docSnapshot = await getDoc(archivedDocRef);

      if (docSnapshot.exists()) {
        return {
          id: docSnapshot.id,
          isArchived: true,
          ...docSnapshot.data(),
        };
      }

      throw new Error("Job not found in both active and archived collections.");
    },
    {
      enabled: !!jobId,
    }
  );
}


export function useAllListingsJobForNew(companyId?: any) {
  return useQuery(
    ["JobDetailsNewCollection", companyId],
    async () => {
      // Create the base query
      let q = query(
        collection(db, "JobDetailsNewCollection"),
        where("jobState", "==", "live"),
      );

      // Add companyId filter if provided
      if (companyId) {
        q = query(
          collection(db, "JobDetailsNewCollection"),
          where("jobState", "==", "live"),
          where("companyId", "==", companyId),
        );
      }

      const querySnapshot = await getDocs(q);
      const listings = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return listings;
    },
    {
      enabled: true, // Ensure the query runs even when companyId is null or undefined
    },
  );
}

export async function getAllLiveJobsForCompany(companyId?:any) {
  const baseCollection = collection(db, "JobDetailsNewCollection");

  let q = query(baseCollection, where("jobState", "==", "live"));

  if (companyId) {
    q = query(baseCollection, where("jobState", "==", "live"), where("companyId", "==", companyId));
  }

  const querySnapshot = await getDocs(q);

  const listings = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return listings;
}

export function useAllListingsJobForEmp(companyId: any) {
  return useQuery(
    ["JobDetailsNewCollection", companyId],
    async () => {
      // Create the base query
      let q = query(
        collection(db, "JobDetailsNewCollection"),
        where("jobState", "==", "live"),
      );

      // Add companyId filter if provided
      if (companyId) {
        q = query(
          collection(db, "JobDetailsNewCollection"),
          where("companyId", "==", companyId),
        );
      }

      const querySnapshot = await getDocs(q);
      const listings = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return listings;
    },
    {
      enabled: true, // Ensure the query runs even when companyId is null or undefined
    },
  );
}



export function useAllListingsJobForNewByCompanyId(
  companyId: any,
  companyName: string,
) {
  return useQuery(
    ["JobDetailsNewCollection", companyId, companyName],
    async () => {
      // First query: fetch jobs with companyId and jobState as 'live'
      const baseQuery = query(
        collection(db, "JobDetailsNewCollection"),
        where("jobState", "==", "live"),
        where("companyId", "==", companyId),
      );
      const baseSnapshot = await getDocs(baseQuery);
      const baseListings = baseSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((listing: any) => listing.solutionType !== "bpo");

      let additionalListings: any = [];
      // Second query: fetch jobs with solutionType as 'bpo' if the companyName is 'Instantteams'
      if (companyName.toLowerCase().startsWith("instant")) {
        const additionalQuery = query(
          collection(db, "JobDetailsNewCollection"),
          where("jobState", "==", "live"),
          where("solutionType", "==", "bpo"),
        );
        const additionalSnapshot = await getDocs(additionalQuery);
        additionalListings = additionalSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      // Combine results from both queries
      return [...baseListings, ...additionalListings];
    },
    {
      enabled: !!companyId,
    },
  );
}

export function useListingJobByDocId(docId: any) {
  return useQuery(
    ["JobDetailsNewCollection", docId],
    async () => {
      // Fetch the document by its ID
      const docRef = doc(db, "JobDetailsNewCollection", docId);
      const docSnapshot = await getDoc(docRef);

      if (docSnapshot.exists()) {
        return {
          id: docSnapshot.id,
          ...docSnapshot.data(),
        };
      } else {
        throw new Error("Document not found");
      }
    },
    {
      enabled: !!docId, // Ensure the query runs only when docId is provided
    },
  );
}
